import { ContextMenuItem, Table } from 'redesign';
import { RenderUnsettledUI, SmallError, SmallLoader } from 'shared/components';
import { useGetCancelationDialog } from 'shared/components/shift-detail-modal/hooks/shared/cancelation-dialog/cancelation-dialog';
import { PartnerClaimantsQueryResponse } from 'partner/shared/services/partner-planner-service.types';
import { IconCloseCircle } from 'assets/img';
import { QueryObserverSuccessResult } from 'react-query';
import { useCallback, useMemo } from 'react';
import { EmptyTableMessage } from 'ogp/components/common/empty-table-message';
import { useTranslation } from 'react-i18next';
import { getPlannerModalTableDefinition } from './definition/use-get-planner-modal-table-definition';
import { getPlannerModalTableData } from './definition/get-planner-modal-table-data';
import { usePartnerGetClaimants } from '../hooks/use-partner-get-claimants';

export const PlannerClaimantTableDataProvider = ({ shiftId }: { shiftId: string }) => {
  const query = usePartnerGetClaimants(shiftId);

  if (query.status !== 'success') {
    return (
      <RenderUnsettledUI
        data={query}
        renderLoading={<SmallLoader />}
        renderError={(error) => <SmallError error={error} />}
      />
    );
  }

  return query.data.items.length === 0 ? (
    <EmptyTableMessage
      title="Er zijn geen flexwerkers ingeschreven!"
      subtitle="Er hebben zich nog geen flexwerkers aangemeld voor deze shift."
    />
  ) : (
    <PlannerClaimantTable query={query} />
  );
};

const PlannerClaimantTable = ({
  query,
}: {
  query: QueryObserverSuccessResult<PartnerClaimantsQueryResponse, unknown>;
}) => {
  const { t } = useTranslation('hq-screen-specific');
  const flatData = query.isSuccess && query.data.items;
  const tableData = useMemo(() => getPlannerModalTableData(flatData), [flatData]);
  const columns = getPlannerModalTableDefinition(t);
  const { handleCancelShiftClaim } = useGetCancelationDialog();

  const getRowActions = useCallback(({ shiftClaimId }) => {
    return [
      <ContextMenuItem onClick={() => handleCancelShiftClaim(shiftClaimId)} key="shiftClaimId">
        <IconCloseCircle />
        Annuleren met reden
      </ContextMenuItem>,
    ];
  }, []);

  return (
    <Table
      stickyHeader
      columns={columns}
      data={tableData}
      getRowActions={getRowActions}
      data-testid="planner-claimant-table"
    />
  );
};
